// Placement data helpers – extracted from PlacementDetailsTableService
import { html } from 'lit';
import { AlertMessagesService } from '../../../shared';

export const prepareCopyPayload = (placementObj, omniProfile = {}) => {
  const isOverride = placementObj["override_taxonomy"];
  let name = (isOverride ? placementObj['overriden_placement_taxonomy'] : placementObj['placement_taxonomy']);
  const payload = {
    "packageType": placementObj.package_type,
    "placementCost": {
      "unitAmount": placementObj.unit_amount,
      "plannedCost": { "amount": placementObj.planned_cost },
      "rate": { "amount": placementObj.rate }
    },
    "placementNumber": null,
    "placementTaxonomy": name,
    "placementTaxonomyDisplay": placementObj.placement_taxonomy_display,
    "overridenPlacementTaxonomy": placementObj.overriden_placement_taxonomy,
    "overridenPlacementTaxonomyDisplay": placementObj.overriden_placement_display_taxonomy,
    "parameterCode": placementObj.parameter_code,
    "parameterCodeTaxonomyDisplay": placementObj.parameter_code_taxonomy_display,
    "parameterCodeTaxonomySchema": placementObj.parameter_code_taxonomy_schema,
    "parentId": placementObj.parent_id?.toString(),
    "taxonomySchema": placementObj.taxonomy_schema,
    "placementType": placementObj.placement_type,
    "buyCategory": placementObj.buy_category,
    "unitType": placementObj.unit_type,
    "costMethod": placementObj.cost_method,
    "costType": placementObj.cost_type,
    "site": placementObj.site,
    "dimensionsPosition": placementObj.dimensions_position,
    "dimensions": placementObj.dimensions,
    "flightStart": placementObj.flight_start,
    "flightEnd": placementObj.flight_end,
    "servedBy": placementObj.served_by,
    "dataSource": placementObj.data_source,
    "primaryPlacement": placementObj.primary_placement,
    "marketValue": placementObj.market_value,
    "provider": placementObj.provider,
    "createdBy": omniProfile?.email,
    "overrideTaxonomy": isOverride,
    "id": placementObj.id?.toString()
  };
  return payload;
};

export const getOmniIconDisableOptions = (selectedItems) => {
  let iconMenuDisabled = {};
  if (!selectedItems.length) {
    iconMenuDisabled["disabled"] = true;
  }
  return iconMenuDisabled;
};

export const copyTextToClipboard = (textToCopy) => {
  navigator.clipboard.writeText(textToCopy)
    .then(() => {
      AlertMessagesService.showSuccessToastMessage("Copied to clipboard!");
    })
    .catch(function (err) {
      AlertMessagesService.showErrorToastMessage("Failed to copy to clipboard", err);
    });
};

export const createEmptyChannelsData = (campaignObj) => {
  let nestedData = [];
  campaignObj?.channels?.forEach((channel, index) => {
    let channelObj = {
      "placement_taxonomy": channel.channel_name,
      "placement_display_name": channel.channel_display_name,
      title: "Channel",
      planned_cost: "",
      planned_cost_local: "",
      unit_amount: 0,
      id: "channelRow" + index,
      nestedData: [],
    };
    channel.publishers.forEach((publisher, publisherIndex) => {
      if (publisher?.publisher_type !== "Ad Serving" && publisher?.publisher_type !== "Ad Verification") {
        let publisherObj = {
          "placement_taxonomy": publisher?.publisher_name?.toUpperCase(),
          "placement_display_name": publisher?.publisher_display_name,
          title: "Publisher",
          planned_cost: "",
          planned_cost_local: "",
          unit_amount: 0,
          channel: channel.channel_name,
          id: "channelChild" + publisherIndex + index,
        };
        channelObj.nestedData.push(publisherObj);
      }
    });
    if (channelObj.nestedData.length > 0)
      nestedData.push(channelObj);
  });
  return nestedData;
};

export const convertToNestedStructure = (data, packageType = '') => {
  return new Promise(function (resolve, reject) {
    const result = [];
    const idMap = {};
    for (const item of data) {
      const id = item.id;
      idMap[id] = { ...prepareCopyPayload(item) };
    }
    data?.forEach((item, itemIndex) => {
      const parentId = item.parent_id;
      if (parentId) {
        if (idMap[parentId]) {
          if (!idMap[parentId].childLineItems) {
            idMap[parentId].childLineItems = [];
          }
          idMap[parentId].childLineItems.push(idMap[item.id]);
        }
      } else {
        result.push(idMap[item.id]);
      }
      if (packageType === item?.package_type && parentId && ['DSP'].includes(item?.package_type)) {
        result.push(idMap[item.id]);
      }
      if (itemIndex + 1 === data?.length) {
        resolve(result);
      }
    });
  });
};

export const removeKey = (obj, key) => {
  if (!obj || typeof obj !== 'object') return;
  if (Array.isArray(obj)) {
    obj.forEach(item => removeKey(item, key));
  } else {
    delete obj[key];
    Object.values(obj).forEach(value => removeKey(value, key));
  }
};

export const getDate = (dateInput = '') => {
  let today;
  if (dateInput) {
    today = new Date(dateInput);
  } else {
    today = new Date();
  }
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  const hours = today.getHours().toString().padStart(2, '0');
  const minutes = today.getMinutes().toString().padStart(2, '0');
  const seconds = today.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const removeEmptyNestedData = (data) => {
  return data.map((item) => {
    if (Array.isArray(item.nestedData)) {
      const removeNestedKey = removeEmptyNestedData(item.nestedData);
      if (removeNestedKey && removeNestedKey.length > 0) {
        item.nestedData = removeNestedKey;
      } else {
        delete item.nestedData;
      }
    }
    return item;
  });
};

export const getPublisherFees = (data, fees, item) => {
  const publisherData = data?.filter(item1 => item1?.site === item?.placement_taxonomy && item1?.placement_type === item?.channel);
  const publisherPlacements = publisherData?.filter(item => ["Standalone", "Package", "ProgrammaticPackage"].includes(item?.package_type));
  const publisherAdVerificationFees = publisherPlacements.reduce((total, item) => total + (item[fees] || 0), 0);
  return publisherAdVerificationFees ? publisherAdVerificationFees?.toLocaleString('en-US', { style: "currency", currency: "USD" }) : '';
};

export const getChannelFees = (data, fees, channel) => {
  const channelData = data?.filter(item => item?.placement_type === channel);
  const channelPlacements = channelData?.filter(item => ["Standalone", "Package", "ProgrammaticPackage"].includes(item?.package_type));
  const channelAdVerificationFees = channelPlacements.reduce((total, item) => total + (item[fees] || 0), 0);
  return channelAdVerificationFees ? channelAdVerificationFees?.toLocaleString('en-US', { style: "currency", currency: "USD" }) : '';
};

export const getSelectedTier = (campaignObj, placementType, supplier) => {
  const selectedChannel = campaignObj?.channels?.find(channel => channel?.channel_name?.toLowerCase() === placementType?.toLowerCase());
  const selectedPubliser = selectedChannel?.publishers?.find(publisher => publisher?.publisher_name?.toLowerCase()?.replace(/ +/g, "") === supplier?.toLowerCase()?.replace(/ +/g, ""));
  return selectedPubliser;
};

export const updateParentTier = (item, parentTierType, parentTierId) => {
  item['parent_tier_type'] = parentTierType || '';
  item["parent_tier_id"] = parentTierId || '';
};

export function isEditableInlineParameterCode(title, placement) {
  if (placement?.parent_id && ['Publisher', 'Supplier Code', 'Supplier', 'Buy Type'].includes(title)) {
    return true;
  } else if (title === 'Cost Method') {
    return true;
  } else {
    return false;
  }
}

export const onPlacementCopySuccess = (result, resetStateAndSendAlerts) => {
  let respMsgArr = [];
  result?.data?.result.forEach((copyResult, index) => {
    if (copyResult?.status === 'success') {
      respMsgArr.push({ type: "success", title: `Placement is copied`, "bodyMsgArr": [html`Placement name:<br/>${copyResult?.placementTaxonomy}`] });
      if (copyResult?.childLineItems?.length) {
        copyResult?.childLineItems.forEach((childItem) => {
          if (childItem?.status === 'success') {
            respMsgArr.push({ type: "success", title: `Placement is copied`, "bodyMsgArr": [html`Placement name:<br/>${childItem?.placementTaxonomy}`] });
          }
          if (childItem?.childLineItems?.length) {
            childItem?.childLineItems.forEach((nestedChildItem) => {
              if (nestedChildItem?.status === 'success') {
                respMsgArr.push({ type: "success", title: `Placement is copied`, "bodyMsgArr": [html`Placement name:<br/>${nestedChildItem?.placementTaxonomy}`] });
              }
            });
          }
        });
      }
    } else {
      respMsgArr.push({ type: "danger", title: "Placement is not copied", "bodyMsgArr": [html`Placement name:<br/>${copyResult?.placementTaxonomy}`, html`Error description:<br/>${copyResult?.validation}`] });
    }
    if (index + 1 === result?.data?.result.length) {
      resetStateAndSendAlerts(respMsgArr);
    }
  });
};

export const onPlacementCopyFailure = (error, resetSelctedItems) => {
  let respMsgArr = [];
  error?.data?.result.forEach((copyResult, index) => {
    if (copyResult?.status === 'error') {
      respMsgArr.push({ type: "danger", retryBtnText: 'Retry', retryBtnEvent: () => onPlacementCopyFailure(error, resetSelctedItems), title: `Placement is not copied`, "bodyMsgArr": [html`Placement name:<br/>${copyResult?.placementTaxonomy}`, html`Error description:<br/>${copyResult?.validation}`] });
      if (copyResult?.childLineItems?.length) {
        copyResult?.childLineItems.forEach((childItem) => {
          if (childItem?.status === 'error') {
            respMsgArr.push({ type: "danger", retryBtnText: 'Retry', retryBtnEvent: () => onPlacementCopyFailure(error, resetSelctedItems), title: `Placement is not copied`, "bodyMsgArr": [html`Placement name:<br/>${childItem?.placementTaxonomy}`, html`Error description:<br/>${childItem?.validation}`] });
          }
          if (childItem?.childLineItems?.length) {
            childItem?.childLineItems.forEach((nestedChildItem) => {
              if (nestedChildItem?.status === 'error') {
                respMsgArr.push({ type: "danger", retryBtnText: 'Retry', retryBtnEvent: () => onPlacementCopyFailure(error, resetSelctedItems), title: `Placement is not copied`, "bodyMsgArr": [html`Placement name:<br/>${nestedChildItem?.placementTaxonomy}`, html`Error description:<br/>${nestedChildItem?.validation}`] });
              }
            });
          }
        });
      }
      if (index + 1 === error?.data?.result?.length) {
        AlertMessagesService.showMultipleAlerts(respMsgArr);
        resetSelctedItems();
      }
    }
  });
};
